"use client";

import { CheckCircle2 } from "lucide-react";
import { useCallback } from "react";

import { stateListItems } from "@/app/(discover)/components/state-selector/constants";
import { StateCard } from "@/app/(discover)/components/state-selector/state-card";
import AuthPaywall from "@/components/shared/auth-paywall/auth-paywall";
import { withAuthGate } from "@/components/shared/auth-paywall/with-auth-gate";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog";
import { authTestIds } from "@/lib/data-test-ids/components.data-test-ids";
import { stateSelectorDataTestIds } from "@/lib/data-test-ids/state-selector.data-test-ids";
import { defaultState, type StateConfig } from "@/lib/states-config";
import { User } from "@/lib/types/auth";
import { useAuth } from "@/stores/use-auth";

export interface StateSelectorDialogProps {
  isOpen: boolean;
  onClose?: () => void;
  onSelect?: () => void;
  currentState?: StateConfig;
}

export function StateSelectorDialog({
  isOpen,
  onClose,
  onSelect,
  currentState,
}: StateSelectorDialogProps) {
  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open && onClose) {
        onClose();
      }
    },
    [onClose],
  );

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogPortal>
        <div className="fixed inset-0 bg-foreground/40 backdrop-blur-sm z-50" />
        <DialogContent
          className="sm:max-w-md max-h-[75vh] sm:max-h-[80vh] overflow-hidden"
          data-testid={stateSelectorDataTestIds.DIALOG}
          showCloseButton={!!onClose}
        >
          <StateSelectorDialogContentWrapper currentState={currentState}>
            <DialogHeader>
              <DialogTitle data-testid={stateSelectorDataTestIds.DIALOG_TITLE}>
                Select Your State
              </DialogTitle>
              <DialogDescription
                data-testid={stateSelectorDataTestIds.DIALOG_DESCRIPTION}
              >
                Choose from our official partner state job boards
              </DialogDescription>
            </DialogHeader>

            <StateSelectorContent
              currentState={currentState}
              onSelect={onSelect}
            />
          </StateSelectorDialogContentWrapper>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}

export const StateSelectorAuthGate = withAuthGate(
  ({ children }: { children: React.ReactNode; user: User }) => <>{children}</>,
  {
    customPaywall: (
      <AuthPaywall
        className="border-none py-0"
        testId={authTestIds.SIGN_IN_STATE_SELECTOR}
      />
    ),
  },
);

export const StateSelectorDialogContentWrapper = ({
  children,
  currentState,
}: {
  currentState?: StateConfig;
  children: React.ReactNode;
}) => {
  if (!currentState) return <>{children}</>;
  return <StateSelectorAuthGate>{children}</StateSelectorAuthGate>;
};

export interface StateSelectorContentProps {
  currentState?: StateConfig;
  onSelect?: () => void;
}

export function StateSelectorContent({
  currentState,
  onSelect,
}: StateSelectorContentProps) {
  const { user } = useAuth();
  let stateList = [...stateListItems];
  if (!currentState && !user) {
    stateList = stateList.filter((item) => item.state !== defaultState);
  }

  return (
    <div className="space-y-3 overflow-y-auto max-h-[calc(75vh-160px)] sm:max-h-[calc(80vh-180px)]">
      <InfoBanner />

      <div
        className="space-y-2"
        data-testid={stateSelectorDataTestIds.STATE_LIST}
      >
        {stateList.map((item) => (
          <div
            key={item.state.route}
            className={item.showSeparator ? "border-t border-border pt-3" : ""}
          >
            <StateCard
              state={item.state}
              subtitle={
                item.showSeparator ? "Browse all states" : "Educator Job Board"
              }
              isSelected={item.state.route === currentState?.route}
              onClick={onSelect}
            />
          </div>
        ))}
      </div>

      <p
        className="text-center text-xs text-ink-faint pt-2"
        data-testid={stateSelectorDataTestIds.FOOTER_TEXT}
      >
        More states coming soon! Nimble Discover is expanding nationwide.
      </p>
    </div>
  );
}

function InfoBanner() {
  return (
    <div className="flex items-start gap-3 p-3 rounded-xl bg-surface-subtle border border-border">
      <div className="w-8 h-8 rounded-full bg-border flex items-center justify-center flex-shrink-0">
        <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="pt-0.5">
        <p className="font-semibold text-foreground text-sm">
          Official Partner State Job Boards
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          Nimble Discover is the official job board platform for these states.
        </p>
      </div>
    </div>
  );
}
